// MainLayout.jsx
import { cloneElement, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import "./MainLayout.scss";
import { Grid } from "antd";

import Logo from "../../assets/appLogo";
import DummyUser from "../../assets/SampleUser.jpg";
import NavbarIcon from "../../assets/icons/navbarIcons/NavbarIcon";
import LogoutIcon from "../../assets/icons/navbarIcons/LogoutIcon";

import {
  ReadOutlined, CloseOutlined,
  UserSwitchOutlined,
  HomeOutlined,
  SettingOutlined
} from "@ant-design/icons";

import CustomModal from "../CustomModal/CustomModal";

const { useBreakpoint } = Grid;
const MENUS = [
  { key: "dashboard", text: "Dashboard", icon: <NavbarIcon />, path: "/dashboard" },
  { key: "assessment", text: "Assessment", icon: <ReadOutlined />, path: "/assessment" },
  { key: "manage-team", text: "Manage Team", icon: <UserSwitchOutlined />, path: "/manage-team" },
  { key: "manage-branch", text: "Manage Branch", icon: <HomeOutlined />, path: "/manage-branch" },
  { key: "settings", text: "Settings", icon: <SettingOutlined />, path: "/settings" },
];

const MainLayout = () => {
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const screens = useBreakpoint();
  const isPhone = !screens.md;
  const location = useLocation();
  const navigate = useNavigate();
  const storedUser = sessionStorage.getItem("user");
  const userData = storedUser ? JSON.parse(storedUser) : null;
  const role = sessionStorage.getItem("role");
  const menuItems = useMemo(() => {
    // Filter menus based on user role
    if (role === "Staff") {
      // Staff should not see Manage Team or Manage Branch
      return MENUS.filter((m) => m.key !== "manage-team" && m.key !== "manage-branch");
    }
    if (role === "BranchAdmin") {
      // BranchAdmin should not see Manage Branch
      return MENUS.filter((m) => m.key !== "manage-branch");
    }
    // Default: show all menus
    return MENUS;
  }, [role]);
  const openMobileNav = () => setMobileNavOpen(true);
  const closeMobileNav = () => setMobileNavOpen(false);

  console.log("role", role);

  useEffect(() => {
    const parent = menuItems.find((m) =>
      m.children?.some((c) => location.pathname.startsWith(c.path))
    );
    if (parent) setOpenKey(parent.key);
  }, [location.pathname, menuItems]);

  useEffect(() => {
    if (!isPhone) return;
    document.body.style.overflow = mobileNavOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileNavOpen, isPhone]);

  const confirmLogout = () => {
    sessionStorage.clear();
    setShowLogoutModal(false);
    navigate("/");
  };

  const toggleOpen = (key) => {
    setOpenKey((prev) => (prev === key ? null : key));
  };

  const handleParentClick = (item) => {
    const hasChildren = !!item.children?.length;
    if (hasChildren) {
      toggleOpen(item.key);
      return;
    }
    if (item.path) {
      navigate(item.path);
      if (isPhone) closeMobileNav();
    }
  };

  const handleChildClick = (path) => {
    navigate(path);
    if (isPhone) closeMobileNav();
  };

  const renderMenuList = () => (
    <ul className="sidebar1_center_menu">
      {menuItems.map((item) => {
        const hasChildren = !!item.children?.length;

        const isChildActive = item.children?.some((c) =>
          location.pathname.startsWith(c.path)
        );

        const isActive =
          (item.path && location.pathname.startsWith(item.path)) ||
          isChildActive;

        const isOpen = openKey === item.key;

        const coloredIcon = cloneElement(item.icon, {
          style: {
            color: isActive ? "#ffffff" : "#919CB4",
            fontSize: "18px",
            fontWeight: 700,
          },
        });

        return (
          <li
            key={item.key}
            className={`sidebar1_center_menu_row ${isActive ? "active" : ""}`}
          >
            <button
              type="button"
              className={`sidebar1_center_menu_btn ${isActive ? "is-active" : ""
                }`}
              onClick={() => handleParentClick(item)}
              aria-expanded={hasChildren ? isOpen : undefined}
            >
              <span className="sidebar1_center_menu_icon">{coloredIcon}</span>
              <span className="sidebar1_center_menu_label">{item.text}</span>
              {hasChildren && (
                <span className={`submenu_caret ${isOpen ? "open" : ""}`}>
                  <CaretDownOutlined />
                </span>
              )}
            </button>

            {hasChildren && isOpen && (
              <ul className="sidebar1_submenu">
                {item.children.map((child) => {
                  const childActive = location.pathname.startsWith(child.path);
                  return (
                    <li key={child.key} className="sidebar1_submenu_row">
                      <button
                        type="button"
                        className={`sidebar1_submenu_btn ${childActive ? "is-active" : ""
                          }`}
                        onClick={() => handleChildClick(child.path)}
                      >
                        {child.icon && (
                          <span className="sidebar1_submenu_icon">
                            {cloneElement(child.icon, {
                              style: { fontSize: "16px" },
                            })}
                          </span>
                        )}
                        <span>{child.text}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="mainlayout">
      {/* ===================== Desktop Sidebar ===================== */}
      {!isPhone && (
        <aside className="sidebar1" aria-label="Main sidebar">
          <div className="sidebar1_top">
            <h1>Main Menu</h1>
          </div>

          <nav className="sidebar1_center" aria-label="Main">
            {renderMenuList()}
          </nav>

          <div className="sidebar1_bottom">
            <button
              type="button"
              className="sidebar1_logout_btn"
              onClick={() => setShowLogoutModal(true)}
            >
              <span className="sidebar1_logout_text">Logout</span>
              <span className="sidebar1_logout_icon">
                <LogoutIcon />
              </span>
            </button>
          </div>
        </aside>
      )}

      {/* ===================== Mobile Drawer Sidebar ===================== */}
      {isPhone && (
        <>
          <div
            className={`mobile-sidebar-backdrop ${mobileNavOpen ? "open" : ""}`}
            onClick={closeMobileNav}
          />

          <aside
            className={`sidebar1 mobile-drawer ${mobileNavOpen ? "open" : ""}`}
            aria-label="Mobile sidebar"
          >
            <div className="sidebar1_top">
              <h1>Main Menu</h1>
              <button
                className="sidebar1_toggle"
                onClick={closeMobileNav}
                type="button"
                aria-label="Close menu"
              >
                <CloseOutlined />
              </button>
            </div>

            <nav className="sidebar1_center" aria-label="Main">
              {renderMenuList()}
            </nav>

            <div className="sidebar1_bottom">
              <button
                type="button"
                className="sidebar1_logout_btn"
                onClick={() => setShowLogoutModal(true)}
              >
                <span className="sidebar1_logout_text">Logout</span>
                <span className="sidebar1_logout_icon">
                  <LogoutIcon />
                </span>
              </button>
            </div>
          </aside>
        </>
      )}

      {/* ===================== Right Pane ===================== */}
      <div className="mainlayout_right">
        <header className="mainlayout_header">
          <div className="header_left">
            {isPhone && (
              <button
                className="header_navbtn"
                type="button"
                aria-label="Open menu"
                onClick={openMobileNav}
              >
                <NavbarIcon />
              </button>
            )}

            <div className="brand">
              <img src={Logo} alt="Logo" className="brand_logo" />
            </div>
          </div>

          <div className="header_right">
            <button className="user_btn" type="button" onClick={() => navigate("/settings")}>
              <img src={userData?.profilePicture || DummyUser} alt="User Avatar" className="user_avatar" style={{ objectFit: 'cover' }} />
              <div className="user_meta">
                <span className="user_name">
                  {userData?.userName || "Admin"}
                </span>
                <span className="user_role" style={{ fontSize: '12px', color: '#64748b', display: 'block', textAlign: 'left', lineHeight: 1.2 }}>
                  {role}
                </span>
              </div>
            </button>

            {isPhone && (
              <button
                className="icon_btn logout_btn_mobile"
                aria-label="Logout"
                type="button"
                onClick={() => setShowLogoutModal(true)}
                title="Logout"
              >
                <LogoutIcon />
              </button>
            )}
          </div>
        </header>

        <main className="mainlayout_content">
          <Outlet />
        </main>
      </div>

      {/* ===================== Logout Modal ===================== */}
      <CustomModal
        open={showLogoutModal}
        title="Confirm Logout"
        onClose={() => setShowLogoutModal(false)}
        showPrimary
        showDanger
        primaryText="Logout"
        dangerText="Cancel"
        onPrimary={confirmLogout}
        onDanger={() => setShowLogoutModal(false)}
        primaryProps={{ variant: "danger" }}
        maskClosable={false}
        centered
      >
        <p style={{ margin: 0 }}>
          Are you sure you want to logout? You’ll need to sign in again to
          continue.
        </p>
      </CustomModal>
    </div>
  );
};

export default MainLayout;
